/**
 * Link Tracking Parameters Utility Functions
 *
 * These utilities help normalize strings and append tracking parameters to URLs
 * for analytics and marketing purposes.
 */

export interface LinkTrackingParams {
  enabled: boolean;
  brijParams: {
    enabled: boolean;
    experienceId: boolean;
    experienceName: boolean;
    experienceTitle: boolean;
  };
  utmParams: {
    enabled: boolean;
    source: string;
    medium: string;
    campaign: string;
    term: string;
    content: string;
    id: string;
    sourcePlatform: string;
    creativeFormat: string;
    marketingTactic: string;
  };
  customParams: {
    enabled: boolean;
    paramString: string;
  };
}

export interface ExperienceData {
  id: string;
  name: string;
  title: string;
}

/**
 * Normalizes a string for use in URL parameters:
 * - Replaces spaces with underscores
 * - Removes or encodes invalid URL characters
 * - Blocks XSS vectors (javascript:, data:, vbscript:, etc.)
 * - Removes dangerous HTML/script characters (<, >, quotes)
 * - Handles emojis and unicode characters safely
 * - Trims whitespace
 *
 * @param value - The string to normalize
 * @returns URL-safe string
 */
function normalizeForUrl(value: string): string {
  if (!value || typeof value !== 'string') {
    return '';
  }

  let normalized = value.trim();

  // Block dangerous protocol schemes that could lead to XSS
  // This prevents javascript:, data:, vbscript:, etc.
  const dangerousProtocols = [
    /javascript\s*:/gi,
    /data\s*:/gi,
    /vbscript\s*:/gi,
    /file\s*:/gi,
    /about\s*:/gi,
    /on\w+\s*=/gi, // Block event handlers like onclick=, onerror=, etc.
  ];

  for (const protocol of dangerousProtocols) {
    if (protocol.test(normalized)) {
      // Remove the dangerous content entirely for security
      normalized = normalized.replace(protocol, '');
    }
  }

  // Remove dangerous characters that could break URLs or HTML
  // Remove: < > " ' ` (angle brackets and quotes that could enable XSS)
  normalized = normalized.replace(/[<>"'`]/g, '');

  // Remove characters that could break URLs: ? # & = + %
  normalized = normalized.replace(/[?#&=+%]/g, '');

  // Replace multiple spaces with single underscore
  normalized = normalized.replace(/\s+/g, '_');

  // Remove control characters and other problematic unicode
  // Keep only printable ASCII, basic unicode (letters, numbers, common punctuation)
  // This will safely handle emojis by encoding them
  // eslint-disable-next-line no-control-regex
  normalized = normalized.replace(/[\u0000-\u001F\u007F-\u009F]/g, '');

  // Encode remaining special characters (including emojis) safely
  // This preserves alphanumeric, underscore, dash, dot, and tilde
  normalized = normalized.replace(/[^\w\-_.~]/g, (char) => {
    try {
      return encodeURIComponent(char);
    } catch {
      // If encoding fails (shouldn't happen), remove the character
      return '';
    }
  });

  // Additional safety: remove any remaining protocol-like patterns
  // This catches edge cases where dangerous content might have survived
  normalized = normalized.replace(/[a-z]+\s*:\s*/gi, (match) => {
    const lowerMatch = match.toLowerCase().trim();
    if (['http:', 'https:', 'mailto:', 'tel:', 'ftp:'].includes(lowerMatch)) {
      return match; // Allow safe protocols if they somehow got through
    }
    return ''; // Remove potentially dangerous protocols
  });

  return normalized;
}

/**
 * Builds query parameters from link tracking settings
 *
 * @param trackingParams - The link tracking configuration
 * @param experience - Experience data (id, name, title)
 * @returns Query parameter string (without leading ? or &)
 */
function buildTrackingParams(
  trackingParams: LinkTrackingParams,
  experience: ExperienceData
): string {
  if (!trackingParams?.enabled) {
    return '';
  }

  const params: string[] = [];

  // Add Custom Parameters (first)
  if (
    trackingParams.customParams?.enabled &&
    trackingParams.customParams.paramString
  ) {
    // Sanitize custom parameters to prevent XSS and URL breaking
    let sanitized = trackingParams.customParams.paramString.trim();

    // Remove leading ? or &
    sanitized = sanitized.replace(/^[?&]+/, '');

    // Parse and sanitize each key-value pair
    // Custom params should be in format: key1=value1&key2=value2
    if (sanitized) {
      try {
        // Split by & and sanitize each parameter
        const paramPairs = sanitized.split('&').filter(Boolean);
        const sanitizedPairs = paramPairs
          .map((pair) => {
            const [key, ...valueParts] = pair.split('=');
            const value = valueParts.join('='); // Rejoin in case value contains =

            if (!key) return null;

            // Sanitize both key and value
            const sanitizedKey = normalizeForUrl(key);
            const sanitizedValue = normalizeForUrl(value);

            if (sanitizedKey) {
              return sanitizedValue
                ? `${sanitizedKey}=${sanitizedValue}`
                : sanitizedKey;
            }
            return null;
          })
          .filter(Boolean) as string[];

        if (sanitizedPairs.length > 0) {
          params.push(sanitizedPairs.join('&'));
        }
      } catch {
        // If parsing fails, skip custom params for safety
      }
    }
  }

  // Add Brij Parameters
  if (trackingParams.brijParams?.enabled) {
    const brij = trackingParams.brijParams;

    if (brij.experienceId && experience.id) {
      params.push(`brij_experience_id=${normalizeForUrl(experience.id)}`);
    }
    if (brij.experienceName && experience.name) {
      params.push(`brij_experience_name=${normalizeForUrl(experience.name)}`);
    }
    if (brij.experienceTitle && experience.title) {
      params.push(`brij_experience_title=${normalizeForUrl(experience.title)}`);
    }
  }

  // Add UTM Parameters
  if (trackingParams.utmParams?.enabled) {
    const utm = trackingParams.utmParams;

    if (utm.source) {
      params.push(`utm_source=${normalizeForUrl(utm.source)}`);
    }
    if (utm.medium) {
      params.push(`utm_medium=${normalizeForUrl(utm.medium)}`);
    }
    if (utm.campaign) {
      params.push(`utm_campaign=${normalizeForUrl(utm.campaign)}`);
    }
    if (utm.term) {
      params.push(`utm_term=${normalizeForUrl(utm.term)}`);
    }
    if (utm.content) {
      params.push(`utm_content=${normalizeForUrl(utm.content)}`);
    }
    if (utm.id) {
      params.push(`utm_id=${normalizeForUrl(utm.id)}`);
    }
    if (utm.sourcePlatform) {
      params.push(`utm_source_platform=${normalizeForUrl(utm.sourcePlatform)}`);
    }
    if (utm.creativeFormat) {
      params.push(`utm_creative_format=${normalizeForUrl(utm.creativeFormat)}`);
    }
    if (utm.marketingTactic) {
      params.push(
        `utm_marketing_tactic=${normalizeForUrl(utm.marketingTactic)}`
      );
    }
  }

  return params.join('&');
}

/**
 * Checks if a URL points to the current page
 *
 * @param url - The URL to check
 * @returns true if the URL is the current page URL
 */
const isCurrentPageUrl = (url: string): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    // Resolve relative URLs to absolute URLs
    const absoluteUrl = new URL(url, window.location.href);
    const currentUrl = new URL(window.location.href);
    return absoluteUrl.origin === currentUrl.origin;
  } catch {
    return false;
  }
};

/**
 * Appends tracking parameters to a URL
 * Handles URLs with or without existing query parameters
 *
 * @param url - The base URL to append parameters to
 * @param trackingParams - The link tracking configuration
 * @param experience - Experience data (id, name, title)
 * @returns URL with tracking parameters appended
 */
export function appendTrackingParamsToUrl(
  url: string,
  trackingParams: LinkTrackingParams | undefined | null,
  experience: ExperienceData
): string {
  if (!url || typeof url !== 'string') {
    return url;
  }

  if (!trackingParams?.enabled) {
    return url;
  }

  // Do NOT append tracking params to the current page URL (internal link to same page)
  if (isCurrentPageUrl(url)) return url;

  // Do NOT append tracking params to presigned/signed URLs (e.g., S3 X-Amz-*).
  // These URLs include a signature that becomes invalid if the querystring changes.
  const hasSignedQueryParams = (searchParams: URLSearchParams): boolean => {
    const keysToBlock = new Set([
      // S3 presigned URLs
      'x-amz-signature',
      'x-amz-credential',
      'x-amz-algorithm',
      'x-amz-date',
      'x-amz-signedheaders',
      'x-amz-expires',
      // CloudFront signed URLs (common)
      'policy',
      'signature',
      'key-pair-id',
    ]);

    // URLSearchParams is case-sensitive, so we must compare case-insensitively.
    for (const key of searchParams.keys()) {
      if (keysToBlock.has(key.toLowerCase())) {
        return true;
      }
    }

    return false;
  };

  try {
    // Parse the URL to safely handle query strings
    const urlObj = new URL(url);
    if (hasSignedQueryParams(urlObj.searchParams)) {
      return url;
    }

    const paramsString = buildTrackingParams(trackingParams, experience);
    if (!paramsString) {
      return url;
    }

    const existingParams = urlObj.search;

    if (existingParams) {
      // URL already has query parameters, append with &
      urlObj.search = existingParams + '&' + paramsString;
    } else {
      // URL has no query parameters, append with ?
      urlObj.search = '?' + paramsString;
    }

    return urlObj.toString();
  } catch {
    // If URL parsing fails, fall back to string manipulation
    // Still avoid touching presigned URLs or current page URLs
    if (
      /[?&](X-Amz-Signature|X-Amz-Credential|X-Amz-Algorithm|X-Amz-Date|X-Amz-SignedHeaders|X-Amz-Expires)=/i.test(
        url
      ) ||
      /[?&](Policy|Signature|Key-Pair-Id)=/i.test(url)
    ) {
      return url;
    }

    // Check if it's the current page URL (for cases where URL parsing failed)
    if (isCurrentPageUrl(url)) return url;

    const paramsString = buildTrackingParams(trackingParams, experience);
    if (!paramsString) {
      return url;
    }

    const separator = url.includes('?') ? '&' : '?';
    return url + separator + paramsString;
  }
}

/**
 * Returns the base URL (scheme + host + path) with all query parameters
 * and fragment stripped. Useful for event payloads where tracking/UTM
 * parameters should not be persisted.
 *
 * Falls back to the original value when the URL cannot be parsed.
 */
export function getBaseUrl(url: string | undefined | null): string {
  if (!url || typeof url !== 'string') {
    return url as string;
  }

  try {
    const parsed = new URL(url);
    return parsed.origin + parsed.pathname;
  } catch {
    // Not a fully-qualified URL — strip everything after `?` or `#`
    return url.split(/[?#]/)[0];
  }
}
